import './polyfills';

import '~/ui/theme/fonts/regola-font.css';
import '~/ui/theme/tailwind-no-preflight.css';
import './app/theming/fonts/material-icons.css';
import './app/theming/fonts/inter.css';
import { LicenseInfo } from '@mui/x-license';
import is from '@sindresorhus/is';
import { createConsola } from 'consola/browser';
import i18next from 'i18next';
import Maybe from 'true-myth/maybe';

import { createAuthorizationProvider } from '~/shared/authorization';
import { createRealClock } from '~/shared/clock/real-clock';
import { appEnvironmentSchema } from '~/shared/environment/environment';
import { createFeatureFlagService } from '~/shared/feature-flag-service';
import { createHttpFetcher } from '~/shared/http-fetcher';
import type { TranslationOverrides } from '~/shared/internationalization';
import { applyOverwrites } from '~/shared/internationalization/translation-overwrites';
import type { Logger } from '~/shared/logger/logger';
import { createQueryClient } from '~/shared/query-client/query-client';
import { createRealtimeEventListenerBuilder } from '~/shared/realtime-event-listener';
import type { ErrorReporter } from '~/shared/validating-http-fetcher/validating-http-fetcher';
import { createValidatingHttpFetcher } from '~/shared/validating-http-fetcher/validating-http-fetcher';

import { tryRenderDevStandalonePage } from '../devtools';
import { App } from './app';
import { initDataDog, setDatadogTenantId } from './data-dog';
import { PrimaryActionButton } from './design/components/button/PrimaryAction';
import { ErrorPage } from './design/components/errors/ErrorPage';
import { createInternationalization } from './internationalization/internationalization';
import { createCustomizationProvider } from './legacy/customization';
import { createConsoleLogger } from './logger/console-logger';
import { render } from './render-app';
import { setFavicon } from './set-favicon';

const setupErrorReporter = (datadogEnabled: boolean, logger: Logger): ErrorReporter => {
    if (datadogEnabled) {
        return initDataDog();
    }

    logger.info('DataDog logging disabled due to environment settings');

    return {
        reportError: (message, error) => logger.error(message, error),
    };
};

export const startApp = async (): Promise<void> => {
    const { t: translate } = i18next;

    if (await tryRenderDevStandalonePage(render)) return;
    try {
        const environment = appEnvironmentSchema.parse(import.meta.env);
        const apiBaseUrl = new URL(environment.apiBaseUrl);
        const apiRealtimeBaseUrl = new URL(environment.apiRealtimeBaseUrl);
        const { enableDataDog, muiXLicenseKey, refetchOnFocus } = environment;
        const logger = createConsoleLogger({ createConsoleTarget: createConsola }, { logLevel: 'silent' });

        const datadogEnabled = import.meta.env.PROD === true || enableDataDog;
        const errorReporter = setupErrorReporter(datadogEnabled, logger);
        const cobrainerApiFetcher = createHttpFetcher(
            {
                errorReporter,
            },
            {
                baseUrl: apiBaseUrl,
                requestHeaders: {
                    get CorrelationId() {
                        return globalThis.crypto.randomUUID();
                    },
                    get RequestId() {
                        return globalThis.crypto.randomUUID();
                    },
                },
            },
        );

        const validatingCobrainerApiFetcher = createValidatingHttpFetcher({
            errorReporter,
            httpFetcher: cobrainerApiFetcher,
        });

        const realtimeEventListenerBuilder = createRealtimeEventListenerBuilder({
            baseUrl: apiRealtimeBaseUrl,
        });

        const realtimeApiFetcher = createHttpFetcher(
            {
                errorReporter,
            },
            {
                baseUrl: apiRealtimeBaseUrl,
            },
        );

        const validatingRealtimeApiFetcher = createValidatingHttpFetcher({
            errorReporter,
            httpFetcher: realtimeApiFetcher,
        });

        const customizationProvider = await createCustomizationProvider(
            { validatingCobrainerApiFetcher },
            {
                location: globalThis.location,
            },
        );

        const {
            id: _customizationId,
            oidcConfiguration,
            tenantId,
            translationOverrides,
        } = customizationProvider.customization.unwrapOrElse(() => {
            throw new Error('No customization found');
        });

        if (datadogEnabled) {
            setDatadogTenantId(tenantId);
        }

        await createInternationalization(
            { i18nProvider: i18next },
            { translationOverrides: translationOverrides as Maybe<TranslationOverrides> },
        );

        const authorizationProvider = await createAuthorizationProvider(
            {
                apiBaseUrl,
                clock: createRealClock(),
                logger,
                validatingHttpFetcher: createValidatingHttpFetcher({
                    errorReporter,
                    httpFetcher: createHttpFetcher({
                        errorReporter,
                    }),
                }),
            },
            {
                oidcConfiguration,
                onAuthorizationReceived: (bearerToken: string) => {
                    const { assumedTenantId, isDefaultCustomization } = customizationProvider;
                    const customizationId = !isDefaultCustomization
                        ? Maybe.just<string>(_customizationId)
                        : Maybe.nothing<string>();

                    validatingCobrainerApiFetcher.requestHeaders = {
                        Authorization: `Bearer ${bearerToken}`,
                        ...(customizationId.isJust ? { 'Customization-Id': customizationId.value } : {}),
                        ...(assumedTenantId.isJust ? { 'Tenant-Id': assumedTenantId.value } : {}),
                    };

                    validatingCobrainerApiFetcher.addHttpErrorHandler((error) => {
                        if (error.response.status === 401) {
                            authorizationProvider.logout();
                        }
                    });

                    realtimeEventListenerBuilder.requestHeaders = {
                        Authorization: `Bearer ${bearerToken}`,
                        ...(assumedTenantId.isJust ? { 'Tenant-Id': assumedTenantId.value } : {}),
                    };

                    realtimeApiFetcher.requestHeaders = {
                        Authorization: `Bearer ${bearerToken}`,
                        ...(assumedTenantId.isJust ? { 'Tenant-Id': assumedTenantId.value } : {}),
                    };
                },
            },
        );

        const queryClient = createQueryClient(refetchOnFocus, authorizationProvider.logout);

        const featureFlagService = createFeatureFlagService({
            queryClient,
            validatingRealtimeApiFetcher,
        });

        // Set history state for initialLoad for BackNavigation
        globalThis.history.replaceState({ initialLoad: true }, globalThis.document.title);

        if (is.string(muiXLicenseKey)) {
            LicenseInfo.setLicenseKey(muiXLicenseKey);
        }

        setFavicon(customizationProvider.customization);

        render(
            <App
                dependencies={{
                    authorizationProvider,
                    clock: createRealClock(),
                    cobrainerApiFetcher,
                    customizationProvider,
                    featureFlagService,
                    initializeFeatureFlagService: ({ userId }) => featureFlagService.initialize(userId),
                    logger,
                    realtimeApiFetcher,
                    realtimeEventListenerBuilder,
                    translate: applyOverwrites(translate),
                    validatingCobrainerApiFetcher,
                    validatingRealtimeApiFetcher,
                }}
                environment={environment}
                queryClient={queryClient}
            />,
        );
        // biome-ignore lint/correctness/noUnusedVariables: suppressed due to migration
    } catch (error: unknown) {
        render(
            <ErrorPage message={translate('errors.fatal.message')} title={translate('errors.fatal.title')}>
                <PrimaryActionButton
                    label={translate('common.errors.action')}
                    onClick={() => globalThis.location.reload()}
                />
            </ErrorPage>,
        );
    }
};

void startApp();
